import { RegistrationDownArrow } from 'assets/icons';
import { PromoConditionsAccordionIcon } from 'assets/icons/PromoConditionsAccordionIcon';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useEffect, useMemo, useState } from 'react';
import { DSAccordionProps } from './DSAccordion.types';
import { useDSAccordion } from './hooks/useDSAccordion';

export const DSAccordion = memo(
	({
		settings,
		title,
		titleTag: TitleTag = 'span',
		description,
		type = 'primary',
		localStorageId,
		children,
		className,
		firstTimeOpen = false,
	}: DSAccordionProps) => {
		const [isOpen, setIsOpen] = useState(settings ? settings.defaultCollapsed : firstTimeOpen);
		const accordionManager = useDSAccordion(settings);
		useEffect(() => {
			if (accordionManager.activeManagment) {
				setIsOpen(accordionManager.collapsed);
			} else if (localStorageId) {
				const savedState = localStorage.getItem(`accordion-${localStorageId}`);
				if (savedState !== null) {
					setIsOpen(JSON.parse(savedState));
				}
			}
		}, [accordionManager]);

		const toggleAccordion = () => {
			if (accordionManager.activeManagment) {
				accordionManager.toggle();
			} else {
				setIsOpen((prev) => {
					const next = !prev;
					if (localStorageId) {
						localStorage.setItem(`accordion-${localStorageId}`, JSON.stringify(next));
					}
					return next;
				});
			}
		};

		const isTitleHeadingTag = TitleTag.startsWith('h');

		const accordionClassNames = cn(`${className} rounded-8 cursor-pointer transition-all`, {
			'bg-gray-100 dark:bg-dark-200 text-gray-900 dark:text-gray-100 ': type === 'primary',
			'bg-transparent': type === 'secondary',
		});

		const iconClassNames = cn('transition-transform transform shrink-0 text-gray-900 dark:text-gray-100', {
			'rotate-180': isOpen,
			'stroke-gray-900 dark:stroke-gray-100': type === 'primary',
			'stroke-primary-500 dark:stroke-info-500': type === 'secondary',
		});

		const headerClassNames = cn('flex justify-between items-center gap-2', {
			' p-4': type === 'primary',
			'p-0': type === 'secondary',
		});

		const contentClassNames = cn({
			'p-4 pt-0 mt-1': type === 'primary',
			'p-0 mt-5': type === 'secondary',
		});

		const icon = useMemo(() => {
			switch (type) {
				case 'primary':
					return <RegistrationDownArrow className={iconClassNames} />;
				case 'secondary':
					return <PromoConditionsAccordionIcon className={iconClassNames} />;
			}
		}, [isOpen]);

		return (
			<div className={accordionClassNames}>
				<div className={headerClassNames} onClick={toggleAccordion}>
					<TitleTag className={cn('m-0', isTitleHeadingTag ? `text-h3 font-bold` : 'text-paragraph-sm font-semibold')}>{title}</TitleTag>
					{icon}
				</div>
				<AnimatePresence initial={true}>
					{isOpen && (
						<motion.div
							key="accordion-content"
							initial={{ height: 0, opacity: 0 }}
							animate={{ height: 'auto', opacity: 1 }}
							exit={{ height: 0, opacity: 0 }}
							transition={{ duration: 0.25, ease: 'easeInOut' }}
						>
							<div className={contentClassNames}>{description ? description : children}</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		);
	},
);
